import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import LogoGKPY from "../components/LogoGKPY";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const HP_RX = /^\+?\d{9,15}$/;
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const today = () => new Date().toISOString().slice(0, 10);
const Sel = ({ value, onChange, options, testId, placeholder = "-- Pilih --" }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full min-h-[44px] rounded-xl border border-input bg-background px-3 text-sm" data-testid={testId}>
    <option value="">{placeholder}</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);
const Field = ({ label, wajib, children, error }) => (
  <div><label className="text-xs font-medium text-slate-600 mb-1 block">{label}{wajib && <span className="text-red-600"> *</span>}</label>{children}{error && <p className="text-xs text-red-600 mt-1">{error}</p>}</div>
);
const labelOf = (f) => f.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()).replace("Nij", "NIJ");

export default function PengajuanPublikPage() {
  const navigate = useNavigate();
  const [master, setMaster] = useState(null);
  const [jenis, setJenis] = useState("");
  const [p, setP] = useState({ nama_lengkap: "", jenis_kelamin: "", nomor_hp: "", alamat: "", status_pemohon: "", nama_panggilan: "", tanggal_lahir: "", email: "", nij: "" });
  const [k, setK] = useState({});
  const [catatan, setCatatan] = useState("");
  const [hp, setHp] = useState("");
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const [idem] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())));

  useEffect(() => { api.get("/pengajuan/master").then((r) => setMaster(r.data)).catch(() => toast.error("Gagal memuat formulir")); }, []);
  const fields = master && jenis ? master.field_khusus[jenis] : [];

  const validate = () => {
    const e = {};
    if (!jenis) e.jenis = "Pilih jenis pelayanan";
    if (!p.nama_lengkap.trim() || p.nama_lengkap.length > 100) e.nama_lengkap = "Wajib diisi, maks 100 karakter";
    if (!p.jenis_kelamin) e.jenis_kelamin = "Wajib dipilih";
    if (!HP_RX.test(p.nomor_hp.trim())) e.nomor_hp = "Hanya angka/+ dan 9–15 karakter";
    if (!p.alamat.trim() || p.alamat.length > 300) e.alamat = "Wajib diisi, maks 300 karakter";
    if (!p.status_pemohon) e.status_pemohon = "Wajib dipilih";
    if (p.email && !EMAIL_RX.test(p.email)) e.email = "Email tidak valid";
    if (p.tanggal_lahir && p.tanggal_lahir > today()) e.tanggal_lahir = "Tidak boleh melebihi hari ini";
    if (catatan.length > 2000) e.catatan = "Maks 2000 karakter";
    fields.forEach((f) => {
      const v = k[f.nama];
      if (f.wajib && (v === undefined || v === "" || v === null)) e[f.nama] = "Wajib diisi";
      else if (v) {
        if (f.maks && String(v).length > f.maks) e[f.nama] = `Maks ${f.maks} karakter`;
        if (f.tipe === "date_past" && v > today()) e[f.nama] = "Tidak boleh melebihi hari ini";
        if (f.tipe === "date_future" && v < today()) e[f.nama] = "Tidak boleh sebelum hari ini";
        if (f.tipe === "int" && (!Number.isInteger(Number(v)) || Number(v) < 1 || Number(v) > 500)) e[f.nama] = "Bilangan bulat 1–500";
      }
    });
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (busy || !validate()) return;
    setBusy(true);
    try {
      const pem = Object.fromEntries(Object.entries(p).map(([a, b]) => [a, b === "" ? null : b]));
      const dk = {};
      fields.forEach((f) => { const v = k[f.nama]; if (v !== undefined && v !== "") dk[f.nama] = f.tipe === "int" ? Number(v) : v; });
      const r = await api.post("/pengajuan/publik", { jenis, pemohon: pem, data_khusus: dk, catatan_pemohon: catatan || null, website: hp || null, idempotency_key: idem });
      navigate("/formulir/konfirmasi", { state: r.data, replace: true });
    } catch (e) { toast.error(errMsg(e, "Pengajuan gagal dikirim")); } finally { setBusy(false); }
  };

  const setPf = (f) => (v) => setP((s) => ({ ...s, [f]: v }));
  const inp = (f, extra = {}) => <Input value={p[f]} onChange={(e) => setPf(f)(e.target.value)} className="min-h-[44px] rounded-xl" data-testid={`pemohon-${f}`} {...extra} />;

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="pengajuan-publik-page">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="text-center"><LogoGKPY className="h-14 mx-auto mb-2" /><h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Formulir Pengajuan Pelayanan</h1><p className="text-sm text-slate-500 mt-1">SIAP GKPY · Isi formulir di bawah, tidak perlu login. Kolom bertanda * wajib diisi.</p></div>
        {!master ? <div className="text-center text-slate-400 py-10">Memuat...</div> : (
          <form onSubmit={submit} className="space-y-5" noValidate>
            <Card className="p-5 rounded-2xl space-y-4">
              <h2 className="font-semibold text-slate-800">1. Jenis Pelayanan</h2>
              <Field label="Jenis Pelayanan" wajib error={errs.jenis}><Sel value={jenis} onChange={(v) => { setJenis(v); setK({}); }} options={master.jenis} testId="jenis-select" /></Field>
            </Card>
            <Card className="p-5 rounded-2xl space-y-4">
              <h2 className="font-semibold text-slate-800">2. Data Pemohon</h2>
              <Field label="Nama Lengkap" wajib error={errs.nama_lengkap}>{inp("nama_lengkap", { maxLength: 100 })}</Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Jenis Kelamin" wajib error={errs.jenis_kelamin}><Sel value={p.jenis_kelamin} onChange={setPf("jenis_kelamin")} options={master.jenis_kelamin} testId="pemohon-jenis_kelamin" /></Field>
                <Field label="Status Pemohon" wajib error={errs.status_pemohon}><Sel value={p.status_pemohon} onChange={setPf("status_pemohon")} options={master.status_pemohon} testId="pemohon-status_pemohon" /></Field>
                <Field label="Nomor HP" wajib error={errs.nomor_hp}>{inp("nomor_hp", { inputMode: "tel", placeholder: "08xxxxxxxxxx" })}</Field>
                <Field label="Email" error={errs.email}>{inp("email", { type: "email" })}</Field>
                <Field label="Nama Panggilan">{inp("nama_panggilan", { maxLength: 100 })}</Field>
                <Field label="Tanggal Lahir" error={errs.tanggal_lahir}>{inp("tanggal_lahir", { type: "date", max: today() })}</Field>
                <Field label="NIJ (jika sudah memiliki)">{inp("nij", { maxLength: 30 })}</Field>
              </div>
              <Field label="Alamat" wajib error={errs.alamat}><Textarea value={p.alamat} onChange={(e) => setPf("alamat")(e.target.value)} maxLength={300} className="rounded-xl" data-testid="pemohon-alamat" /></Field>
              <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" data-testid="honeypot-website" name="website" />
            </Card>
            {jenis && (
              <Card className="p-5 rounded-2xl space-y-4" data-testid="data-khusus-card">
                <h2 className="font-semibold text-slate-800">3. Data {jenis}</h2>
                {fields.map((f) => (
                  <Field key={f.nama} label={labelOf(f.nama)} wajib={f.wajib} error={errs[f.nama]}>
                    {f.tipe === "choice" ? <Sel value={k[f.nama]} onChange={(v) => setK((s) => ({ ...s, [f.nama]: v }))} options={f.opsi} testId={`khusus-${f.nama}`} />
                      : f.tipe === "long" ? <Textarea value={k[f.nama] || ""} onChange={(e) => setK((s) => ({ ...s, [f.nama]: e.target.value }))} maxLength={f.maks || 2000} className="rounded-xl" data-testid={`khusus-${f.nama}`} />
                      : <Input type={f.tipe.startsWith("date") ? "date" : f.tipe === "int" ? "number" : "text"} min={f.tipe === "int" ? 1 : undefined} max={f.tipe === "int" ? 500 : undefined}
                          value={k[f.nama] || ""} onChange={(e) => setK((s) => ({ ...s, [f.nama]: e.target.value }))} maxLength={f.maks || undefined} className="min-h-[44px] rounded-xl" data-testid={`khusus-${f.nama}`} />}
                  </Field>
                ))}
              </Card>
            )}
            <Card className="p-5 rounded-2xl space-y-4">
              <Field label="Catatan Tambahan (opsional)" error={errs.catatan}><Textarea value={catatan} onChange={(e) => setCatatan(e.target.value)} maxLength={2000} className="rounded-xl" data-testid="catatan-pemohon" /></Field>
              <Button type="submit" disabled={busy} className="w-full min-h-[48px] rounded-xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" data-testid="submit-pengajuan-btn">{busy ? "Mengirim..." : "Kirim Pengajuan"}</Button>
            </Card>
          </form>
        )}
      </div>
    </div>
  );
}
